import type { ChatMessage } from '@/types/chat';
import type { TimelineItem } from '../types/session.types';
import type { DisplayExchange, ExchangePhase } from '../types/displayExchange';

/**
 * Le marqueur persisté d'une compaction de la mémoire :
 * `{ role: 'system', content: '', metadata: { kind: 'compaction_checkpoint' } }`.
 * Toute autre ligne système reste invisible.
 */
function isCompactionCheckpoint(message: ChatMessage): boolean {
  return message.role === 'system' && message.metadata?.kind === 'compaction_checkpoint';
}

/** Même item que la compaction live : `StreamingTimeline` le rend en séparateur, hors groupe d'outils. */
function compactionSeparatorItem(message: ChatMessage): TimelineItem {
  const id = `restored-compaction-${message.id}`;
  return {
    type: 'tool',
    id,
    event: {
      id,
      tool: 'context_compaction',
      status: 'completed',
      timestamp: message.createdAt.toISOString(),
    },
  };
}

/**
 * Builds historical DisplayExchange[] from DB-restored messages and tool timelines.
 *
 * This module intentionally handles DB history reconstruction only. Live,
 * pending, and locally committed turns are projected by buildTranscriptExchanges
 * from the conversation transcript store, so they cannot be re-paired with the
 * wrong historical user message during a terminal WebSocket handoff.
 *
 * Messages are split at user message boundaries into exchanges.
 * Each exchange's assistant messages contribute content items (text) and
 * tool items (from toolTimelines), merged and sorted by timestamp.
 *
 * Compaction checkpoints are drawn as a separator AT THEIR POSITION:
 * - followed by a user message (or ending the history) — a compaction between
 *   two turns, manual or not — the separator is its own exchange with
 *   `userMessage: null`, so it never slips under the previous answer's action bar;
 * - followed by assistant rows — an automatic compaction inside a turn — it stays
 *   in that turn's exchange, between the question and the rest of the answer, so
 *   the exchange keeps one feedback anchor and one retry target.
 *
 * All returned exchanges have phase: 'history'.
 */
export function buildHistoricalExchanges(
  messages: ChatMessage[],
  toolTimelines: Map<string, TimelineItem[]>,
): DisplayExchange[] {
  // Other system rows carry nothing the lawyer can read: drop them before
  // pairing so they can neither open an empty exchange nor shift a checkpoint.
  const visibleMessages = messages.filter((msg) => msg.role !== 'system' || isCompactionCheckpoint(msg));
  if (visibleMessages.length === 0) return [];

  // Split messages into exchanges at each user message boundary
  const rawExchanges: ChatMessage[][] = [];
  visibleMessages.forEach((msg, index) => {
    const currentExchange = rawExchanges[rawExchanges.length - 1];
    const next = visibleMessages[index + 1];
    const startsOwnExchange = msg.role === 'user'
      || !currentExchange
      || (isCompactionCheckpoint(msg) && (next === undefined || next.role === 'user'));
    if (startsOwnExchange) {
      rawExchanges.push([msg]);
    } else {
      currentExchange.push(msg);
    }
  });

  return rawExchanges.map((exchange) => {
    const userMsg = exchange.find(m => m.role === 'user') ?? null;
    const assistantMsgs = exchange.filter(m => m.role === 'assistant');

    // Merge content + tools from all assistant messages, sorted by timestamp
    const timedItems: { item: TimelineItem; ts: number }[] = [];

    for (const msg of exchange) {
      const ts = msg.createdAt.getTime();

      if (isCompactionCheckpoint(msg)) {
        timedItems.push({ item: compactionSeparatorItem(msg), ts });
        continue;
      }
      if (msg.role !== 'assistant') continue;

      if (msg.content?.trim()) {
        timedItems.push({
          item: { type: 'content', id: `restored-content-${msg.id}`, content: msg.content },
          ts,
        });
      }

      const tools = toolTimelines.get(msg.id);
      if (tools) {
        tools.forEach((tool, i) => {
          timedItems.push({ item: tool, ts: ts + 1 + i });
        });
      }
    }

    // Array.prototype.sort is stable: rows sharing a millisecond keep the API order.
    timedItems.sort((a, b) => a.ts - b.ts);

    return {
      id: exchange[0]?.id ?? crypto.randomUUID(),
      userMessage: userMsg,
      timeline: timedItems.map(t => t.item),
      phase: 'history' as ExchangePhase,
      // An assistant row can never redefine the dossier of the user turn it
      // answers. Preserve undefined separately from an explicit general null.
      ...(userMsg?.caseId !== undefined ? { caseId: userMsg.caseId } : {}),
      // Anchor for the feedback thumbs: the LAST assistant turn's real DB id.
      lastAssistantMessageId: assistantMsgs[assistantMsgs.length - 1]?.id,
    };
  });
}
